import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Eye, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Orders() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data = [], isLoading } = useQuery({ queryKey: ["orders"], queryFn: api.listOrders });
  const [confirmId, setConfirmId] = useState(null);
  const [convertOrder, setConvertOrder] = useState(null);
  const [cForm, setCForm] = useState({
    delivery_date: "", delivery_address: "", carrier: "",
    tender_name: "", tender_number: "", po_number: "", notes: "",
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteOrder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setConfirmId(null);
      toast.success("Deleted");
    },
  });

  const convertMut = useMutation({
    mutationFn: ({ id, body }) => api.convertOrder(id, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["delivery-orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`DO created: ${data.do_number}`);
      setConvertOrder(null);
      nav(`/delivery-orders/${data.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  function openConvert(o) {
    setConvertOrder(o);
    setCForm({
      delivery_date: new Date().toISOString().slice(0, 10),
      delivery_address: o.client_snapshot?.address || "",
      carrier: "",
      tender_name: "",
      tender_number: "",
      po_number: "",
      notes: "",
    });
  }

  return (
    <div data-testid="orders-page">
      <PageHeader
        overline={t("orders")}
        title={t("orders")}
        actions={
          <Link to="/orders/new">
            <Button data-testid="new-order-btn" className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> {t("new_order")}
            </Button>
          </Link>
        }
      />

      {isLoading ? (
        <p className="text-muted-foreground">{t("loading")}</p>
      ) : data.length === 0 ? (
        <EmptyState
          title={t("no_data")}
          hint={t("new_order")}
          action={
            <Link to="/orders/new">
              <Button data-testid="empty-new-order-btn" className="bg-primary hover:bg-primary/90">
                <Plus className="size-4 mr-1.5" /> {t("new_order")}
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="orders-table-wrap">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">{t("order_number")}</TableHead>
                <TableHead className="overline">{t("client")}</TableHead>
                <TableHead className="overline">{t("order_date")}</TableHead>
                <TableHead className="overline">{t("status")}</TableHead>
                <TableHead className="overline text-right">{t("total")}</TableHead>
                <TableHead className="overline text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell className="font-medium mono">
                    <Link to={`/orders/${o.id}`} className="hover:underline text-primary">{o.order_number}</Link>
                  </TableCell>
                  <TableCell>{o.client_snapshot?.name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{fmtDate(o.order_date)}</TableCell>
                  <TableCell><StatusBadge value={o.status} /></TableCell>
                  <TableCell className="text-right mono font-medium">{fmtMoney(o.total)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Link to={`/orders/${o.id}`}>
                      <Button data-testid={`view-order-${o.id}`} variant="ghost" size="sm">
                        <Eye className="size-3.5" />
                      </Button>
                    </Link>
                    {o.delivery_order_id ? (
                      <Link to={`/delivery-orders/${o.delivery_order_id}`}>
                        <Button data-testid={`view-do-from-order-${o.id}`} variant="ghost" size="sm" className="text-primary">
                          <Truck className="size-3.5" />
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        data-testid={`convert-order-${o.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        onClick={() => openConvert(o)}
                        title={t("convert_to_do")}
                      >
                        <Truck className="size-3.5" />
                      </Button>
                    )}
                    <Button
                      data-testid={`delete-order-${o.id}`}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setConfirmId(o.id)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-order-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-order-ok"
              onClick={() => deleteMut.mutate(confirmId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!convertOrder} onOpenChange={(o) => !o && setConvertOrder(null)}>
        <DialogContent data-testid="convert-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">{t("convert_to_do")}</DialogTitle>
            <DialogDescription className="text-xs">
              {convertOrder?.order_number} → {t("delivery_orders")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">{t("delivery_date")}</Label>
              <Input
                data-testid="convert-delivery-date"
                type="date"
                value={cForm.delivery_date}
                onChange={(e) => setCForm({ ...cForm, delivery_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">{t("carrier")}</Label>
              <Input
                data-testid="convert-carrier"
                value={cForm.carrier}
                onChange={(e) => setCForm({ ...cForm, carrier: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("delivery_address")}</Label>
              <Textarea
                data-testid="convert-delivery-address"
                value={cForm.delivery_address}
                onChange={(e) => setCForm({ ...cForm, delivery_address: e.target.value })}
                rows={2}
              />
            </div>
            <div className="sm:col-span-2 border-t border-border pt-3">
              <p className="overline">{t("tender_po")}</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("tender_name")}</Label>
              <Input
                data-testid="convert-tender-name"
                value={cForm.tender_name}
                onChange={(e) => setCForm({ ...cForm, tender_name: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">{t("tender_number")}</Label>
              <Input
                data-testid="convert-tender-number"
                value={cForm.tender_number}
                onChange={(e) => setCForm({ ...cForm, tender_number: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">{t("po_number")}</Label>
              <Input
                data-testid="convert-po-number"
                value={cForm.po_number}
                onChange={(e) => setCForm({ ...cForm, po_number: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("notes")}</Label>
              <Textarea
                data-testid="convert-notes"
                value={cForm.notes}
                onChange={(e) => setCForm({ ...cForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOrder(null)} data-testid="convert-cancel">
              {t("cancel")}
            </Button>
            <Button
              data-testid="convert-submit"
              className="bg-primary hover:bg-primary/90"
              disabled={convertMut.isPending}
              onClick={() => convertMut.mutate({ id: convertOrder.id, body: cForm })}
            >
              <Truck className="size-4 mr-1.5" /> {t("convert_with_info")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
